"use client";

import { SVGIconProps } from "@faire/slate-icons/types";
import { LinkAsPolymorphicLink } from "@faire/slate/private/link/LinkAsPolymorphicLink";
import { MenuDivider } from "@faire/slate/private/menu/MenuDivider";
import { MenuItemBase } from "@faire/slate/private/menu/MenuItemBase";
import { MenuItemContent } from "@faire/slate/private/menu/MenuItemContent";
import {
  IMenuItemPropsConstraints,
  MenuItemVariant,
} from "@faire/slate/private/menu/types";
import { IWebOnlyProps } from "@faire/slate/private/shared/types";
import React from "react";
import type { LinkProps } from "react-router-dom";

type IProps = {
  /**
   * The icon to display in the menu item.
   */
  Icon?: React.FC<SVGIconProps>;

  /**
   * The label to display in the menu item.
   */
  label?: string;

  /**
   * When static, menu will be rendered as a simple li without button or link.
   */
  static?: boolean;

  /**
   * The variant of the menu item.
   *
   * @default link
   */
  variant?: MenuItemVariant;

  /**
   * **web**
   * The link to navigate to.
   */
  linkTo?: LinkProps["to"];

  /**
   * **web**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;

  /**
   * **web**
   * The anchor element download prop.
   */
  download?: React.AnchorHTMLAttributes<HTMLAnchorElement>["download"];

  /**
   * Whether the menu item is destructive (critical/red text and icon).
   * Only valid for the "link" variant.
   *
   * @default false
   */
  destructive?: boolean;
} & IWebOnlyProps<HTMLElement>;

/**
 * Item of the menu. Menu items can be controlled by the keyboard (arrow keys) and mouse (click) when the menu is opened.
 *
 * Menu items can be of the following types:
 * - link/button - variant "link" or "customSlot"
 * - divider - variant "divider"
 *
 * ```tsx
 * <MenuItem Icon={Icon} label="Menu Item" linkTo="#" />
 * <MenuItem variant="divider" />
 * <MenuItem variant="customSlot" linkTo="#">
 *   <MyCustomMenuItem />
 * </MenuItem>
 * ```
 *
 * By default, the underlying tag of menu item is button or link (when `linkTo` is provided).
 * To render menu item as non-interactive component, for menu titles, for instance, set `static` to `true`.
 *
 * ```tsx
 * <MenuItem static>
 *   <MenuTitle />
 * </MenuItem>
 * ```
 *
 * See the Menu stories for more examples (such as custom interactive content)
 *
 */
export const MenuItem: React.FC<IMenuItemPropsConstraints<IProps>> = ({
  variant,
  static: staticProp,
  Icon,
  label,
  children,
  destructive = false,
  ...props
}) => {
  if (variant === "divider") {
    return <MenuDivider />;
  }

  if (staticProp) {
    return (
      <MenuItemBase {...props} as="div">
        {children}
      </MenuItemBase>
    );
  }

  const as = props.linkTo ? LinkAsPolymorphicLink : "button";

  return (
    <MenuItemBase {...props} as={as} interactive destructive={destructive}>
      {children ?? <MenuItemContent Icon={Icon} label={label} />}
    </MenuItemBase>
  );
};
